import { useEffect, useState, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { apiAdmin, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import Sidebar from "@/components/Sidebar";
import AccountCodeBanner from "@/components/AccountCodeBanner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BusinessContext } from "@/context/BusinessContext";


export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBusinessState] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [newName, setNewName] = useState("");

  const fetchBusinesses = useCallback(async () => {
    try {
      const { data } = await apiAdmin.get("/businesses");
      setBusinesses(data);
      setActiveBusinessState((prev) => {
        const fresh = prev && data.find((b) => b.id === prev.id);
        if (fresh) return fresh;
        const roy = data.find((b) => b.name === "Roy Restaurant") || data[0] || null;
        return roy;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const createBusiness = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await apiAdmin.post("/businesses", { name: newName.trim() });
      toast.success("Negocio creado");
      setOpenNew(false);
      setNewName("");
      await fetchBusinesses();
      setActiveBusinessState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <BusinessContext.Provider
      value={{ businesses, activeBusiness, setActiveBusiness: setActiveBusinessState, refreshBusinesses: fetchBusinesses }}
    >
      <div className="min-h-screen flex cc-admin cc-admin-bg" data-testid="admin-layout">
        <Sidebar email={user?.email} accountCode={user?.account_code} onLogout={onLogout} />

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-16 px-6 cc-admin-bg sticky top-0 z-20 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Brand className="h-7" />
              <div className="h-6 w-px bg-slate-200" />
              <span className="text-xs uppercase tracking-widest text-slate-500">Negocio</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white" data-testid="business-selector-trigger">
                    <span className="font-heading font-semibold text-slate-900">
                      {activeBusiness?.name || "Sin negocio"}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs text-slate-500">Switch business</DropdownMenuLabel>
                  {businesses.map((b) => (
                    <DropdownMenuItem
                      key={b.id}
                      onSelect={() => setActiveBusinessState(b)}
                      data-testid={`business-option-${b.id}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{b.name}</span>
                        {b.description ? (
                          <span className="text-xs text-slate-500 truncate max-w-[200px]">{b.description}</span>
                        ) : null}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setOpenNew(true)} data-testid="business-add-trigger">
                    <Plus className="h-4 w-4 mr-2" /> Agregar negocio
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> System healthy
            </div>
          </header>

          <main className="flex-1 p-8 overflow-x-hidden">
            {activeBusiness ? <Outlet /> : <NoBusiness onCreate={() => setOpenNew(true)} />}
          </main>
        </div>

        {/* New business dialog */}
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogContent data-testid="business-add-dialog">
            <DialogHeader>
              <DialogTitle>Nuevo negocio</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <Label htmlFor="biz-name">Nombre del negocio</Label>
              <Input
                id="biz-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Roy Restaurant"
                data-testid="business-add-name-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenNew(false)} data-testid="business-add-cancel">
                Cancelar
              </Button>
              <Button onClick={createBusiness} className="bg-blue-600 hover:bg-blue-700" data-testid="business-add-submit">
                Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BusinessContext.Provider>
  );
}

function NoBusiness({ onCreate }) {
  return (
    <div className="space-y-6" data-testid="no-business">
      <AccountCodeBanner />
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
        <div className="font-heading text-xl font-semibold text-slate-900">Todavía no tienes un negocio</div>
        <p className="text-sm text-slate-500 mt-1">Crea tu primer negocio para conectar pantallas y subir contenido.</p>
        <Button onClick={onCreate} className="mt-5 bg-[#4C5BD8] hover:bg-[#3F4DC4]" data-testid="no-business-create-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Crear negocio
        </Button>
      </div>
    </div>
  );
}
